import {
  Certificate,
  ClockCounterClockwise,
  CloudArrowDown,
  Faders,
  FlowArrow,
  GithubLogo,
  House,
  Key,
  LockKey,
  Shapes,
  Shield,
  ShieldCheck,
  Textbox,
  Translate,
  UserPlus,
  Users,
  UsersThree,
  WebhooksLogo,
} from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { useAdminCaps } from "@/components/admin/caps";
import { useCanManageFields } from "@/components/planning/fields/scopes";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { SubNav, type SubNavGroup } from "@/components/shell/SubNav";
import { buttonStyles, EmptyState, RowSkeleton } from "@/components/ui";
import { usePermissions } from "@/components/views/api";

export const Route = createFileRoute("/_app/admin")({ component: AdminLayout });

/**
 * Administration shell: a secondary navigation rail (tabs row on small screens) next to the section
 * the URL names. Owners see everything; other people see the sections their permissions open, and
 * anyone without any gets the denied page instead of a half-working screen.
 */
function AdminLayout() {
  const caps = useAdminCaps();
  const fields = useCanManageFields();
  const schema = usePermissions().instance("schema.manage");
  const groups = useMemo<SubNavGroup[]>(() => {
    const people: SubNavGroup["items"] = caps.users
      ? [
          { to: "/admin/users", label: m.adm_nav_users(), icon: Users },
          { to: "/admin/invites", label: m.adm_nav_invites(), icon: UserPlus },
          { to: "/admin/groups", label: m.adm_nav_groups(), icon: UsersThree },
        ]
      : [];
    const access: SubNavGroup["items"] = [
      ...(caps.users || caps.roles ? [{ to: "/admin/roles", label: m.adm_nav_roles(), icon: Shield }] : []),
      ...(caps.users ? [{ to: "/admin/access", label: m.adm_nav_access(), icon: Key }] : []),
    ];
    const instance: SubNavGroup["items"] = [
      ...(caps.audit ? [{ to: "/admin/audit", label: m.adm_nav_audit(), icon: ClockCounterClockwise }] : []),
      ...(caps.webhooks ? [{ to: "/admin/webhooks", label: m.adm_nav_webhooks(), icon: WebhooksLogo }] : []),
      ...(caps.instance
        ? [
            { to: "/admin/instance", label: m.adm_nav_instance(), icon: Faders },
            { to: "/admin/authentication", label: m.adm_nav_authentication(), icon: ShieldCheck },
            { to: "/admin/github", label: m.adm_nav_github(), icon: GithubLogo },
            { to: "/admin/translation", label: m.adm_nav_translation(), icon: Translate },
            { to: "/admin/system", label: m.adm_nav_system(), icon: CloudArrowDown },
            { to: "/admin/license", label: m.adm_nav_license(), icon: Certificate },
          ]
        : []),
    ];
    return [
      { items: [{ to: "/admin", label: m.adm_nav_overview(), icon: House, exact: true }] },
      ...(people.length ? [{ label: m.adm_nav_people(), items: people }] : []),
      ...(access.length ? [{ label: m.adm_nav_access_group(), items: access }] : []),
      ...(fields
        ? [
            {
              label: m.adm_nav_configuration(),
              items: [
                { to: "/admin/fields", label: m.fields_title(), icon: Textbox },
                ...(schema
                  ? [
                      { to: "/admin/types", label: m.sch_types_title(), icon: Shapes },
                      { to: "/admin/workflows", label: m.sch_workflows_title(), icon: FlowArrow },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(instance.length ? [{ label: m.adm_nav_instance_group(), items: instance }] : []),
    ];
  }, [caps.users, caps.roles, caps.audit, caps.instance, caps.webhooks, fields, schema]);

  if (caps.pending) {
    return (
      <AdminShell>
        <RowSkeleton rows={4} />
      </AdminShell>
    );
  }
  if (!caps.any && !fields) return <AdminDenied />;

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <SubNav label={m.adm_nav_label()} groups={groups} />
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

function AdminShell({ children }: { children: ReactNode }) {
  useBreadcrumbs([{ label: m.adm_title() }]);
  return (
    <Page title={m.adm_title()} icon={ShieldCheck}>
      {children}
    </Page>
  );
}

/** Permission denied says what is missing and who can fix it. */
function AdminDenied() {
  useBreadcrumbs([{ label: m.adm_title() }]);
  return (
    <Page title={m.adm_title()} icon={ShieldCheck}>
      <EmptyState
        icon={LockKey}
        title={m.adm_denied_title()}
        description={m.adm_denied_body()}
        action={
          <Link to="/" className={buttonStyles({ variant: "secondary" })}>
            {m.adm_denied_back()}
          </Link>
        }
      />
    </Page>
  );
}
